import { Layout } from "../components/Layout";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Button } from "@/components/Button";
import { useToast } from "@/components/Toast";
import { Skeleton } from "@/components/Skeleton/Skeleton";
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary";
import {
  useRecomputeStorage,
  useRefreshStorageBreakdown,
  useStorageBreakdown,
  useStorageOverview,
  type StorageBreakdown,
  type StorageBreakdownGroup,
  type StorageCategory,
  type StorageCategoryTotals,
} from "@/hooks/useStorage";
import { formatBytes, formatNumber } from "@/utils/formatters";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  CloudArrowUpIcon,
  VideoCameraIcon,
  PhotoIcon,
  FolderIcon,
  FilmIcon,
  ArchiveBoxIcon,
} from "@heroicons/react/24/outline";

// What the table and cards call each group of R2 categories. HLS copies and renditions are
// shown together; downloads, exchange previews, diagrams, trash and the rest are "Other".
const GROUPS: { id: string; label: string; categories: StorageCategory[] }[] = [
  { id: "original", label: "Originals", categories: ["original"] },
  { id: "transcoded", label: "Transcoded film", categories: ["transcoded"] },
  { id: "cover", label: "Cover frames", categories: ["cover"] },
  { id: "hls", label: "HLS / renditions", categories: ["fragmented", "rendition"] },
  {
    id: "other",
    label: "Other",
    categories: ["download", "exchange_preview", "diagram", "trash", "other"],
  },
];

const GROUP_COLORS: Record<string, string> = {
  original: "#3B82F6", // blue
  transcoded: "#10B981", // green
  cover: "#F59E0B", // amber
  hls: "#8B5CF6", // violet
  other: "#6B7280", // gray
};

function sumGroup(categories: StorageCategoryTotals, ids: StorageCategory[]) {
  return ids.reduce(
    (acc, id) => ({
      bytes: acc.bytes + categories[id].bytes,
      count: acc.count + categories[id].count,
    }),
    { bytes: 0, count: 0 }
  );
}

function BreakdownRow({
  label,
  group,
  note,
  bold,
}: {
  label: string;
  group: StorageBreakdownGroup;
  note?: string;
  bold?: boolean;
}) {
  return (
    <tr className={`border-b border-border hover:bg-surface/50 ${bold ? "font-semibold" : ""}`}>
      <td className="py-3 px-4">
        <span className={bold ? "" : "font-medium"}>{label}</span>
        {note && <span className="block text-xs text-secondary font-normal">{note}</span>}
      </td>
      {GROUPS.map((g) => {
        const t = sumGroup(group.categories, g.categories);
        return (
          <td key={g.id} className="text-right py-3 px-4" title={`${formatNumber(t.count)} files`}>
            {formatBytes(t.bytes)}
          </td>
        );
      })}
      <td className="text-right py-3 px-4 font-medium" title={`${formatNumber(group.total_count)} files`}>
        {formatBytes(group.total_bytes)}
      </td>
    </tr>
  );
}

function BreakdownTable({ breakdown }: { breakdown: StorageBreakdown }) {
  const { unattributed } = breakdown;
  const total: StorageBreakdownGroup = {
    categories: breakdown.categories,
    total_bytes: breakdown.total_bytes,
    total_count: breakdown.objects,
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-3 px-4">Team</th>
            {GROUPS.map((g) => (
              <th key={g.id} className="text-right py-3 px-4">
                {g.label}
              </th>
            ))}
            <th className="text-right py-3 px-4">Total</th>
          </tr>
        </thead>
        <tbody>
          {breakdown.teams.map((team) => (
            <BreakdownRow key={team.public_id} label={team.name} group={team} />
          ))}
          <BreakdownRow
            label="Individual users (no team)"
            group={breakdown.individuals}
            note="Films in games that belong to a person, not a team"
          />
          <BreakdownRow
            label="Unattributed"
            group={unattributed}
            note={`${formatBytes(unattributed.reasons.game_not_in_d1.bytes)} from games no longer in the database; ${formatBytes(unattributed.reasons.no_game_in_key.bytes)} that belong to no game (downloads, previews, diagrams, other)`}
          />
          <BreakdownRow label="Bucket total" group={total} bold />
        </tbody>
      </table>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-surface border border-border rounded-md p-3 shadow-lg">
        <p className="text-sm font-medium">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.name.includes("Size") ? formatBytes(entry.value) : formatNumber(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export function StorageOverview() {
  const { data, isLoading, error } = useStorageOverview();
  const breakdownQuery = useStorageBreakdown();
  const refreshBreakdown = useRefreshStorageBreakdown();
  const { toast } = useToast();
  const recompute = useRecomputeStorage();

  const breakdown =
    breakdownQuery.data && breakdownQuery.data.computed ? breakdownQuery.data.breakdown : null;

  // A refresh is read-only on R2 (list calls only), so it needs no confirmation.
  const runRefresh = () =>
    refreshBreakdown.mutate(undefined, {
      onSuccess: (res) =>
        toast({
          title: "Storage breakdown refreshed",
          description: res.computed
            ? `${formatNumber(res.breakdown.objects)} objects in ${formatNumber(res.breakdown.list_calls)} list calls.`
            : undefined,
        }),
      onError: (err) =>
        toast({
          title: "Couldn't refresh the storage breakdown",
          description: err.message,
          variant: "destructive",
        }),
    });

  const runRecompute = () => {
    if (
      !window.confirm(
        "Recompute every team's and team-less user's storage counter now? This is the same job the daily cron runs: each counter is reset to the transcoded size of the sources it owns. No files are deleted."
      )
    )
      return;
    recompute.mutate(
      {},
      {
        onSuccess: (stats) =>
          toast({
            title: "Storage counters recomputed",
            description: `${formatNumber(stats.owners_changed)} of ${formatNumber(stats.owners_scanned)} owners changed (${formatBytes(stats.bytes_before)} to ${formatBytes(stats.bytes_after)}).`,
          }),
        onError: (err) =>
          toast({
            title: "Couldn't recompute storage",
            description: err.message,
            variant: "destructive",
          }),
      }
    );
  };

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    );
  }

  if (isLoading || !data) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-96" />
            <Skeleton className="h-96" />
          </div>
        </div>
      </Layout>
    );
  }

  // Prepare data for charts (only once a breakdown has been computed)
  const groupData = breakdown
    ? GROUPS.map((g) => ({
        id: g.id,
        name: g.label,
        value: sumGroup(breakdown.categories, g.categories).bytes,
      })).filter((g) => g.value > 0)
    : [];

  const topTeamsData = breakdown
    ? breakdown.teams.slice(0, 10).map((team) => ({
        name: team.name,
        size: team.total_bytes,
        files: team.total_count,
      }))
    : [];

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          title="Storage Overview"
          description="Monitor and analyze R2 storage usage across the platform"
          actions={
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={runRefresh}
                disabled={refreshBreakdown.isPending}
              >
                {refreshBreakdown.isPending ? "Refreshing..." : "Refresh breakdown"}
              </Button>
              <Button variant="outline" onClick={runRecompute} disabled={recompute.isPending}>
                {recompute.isPending ? "Recomputing..." : "Recompute usage counters"}
              </Button>
            </div>
          }
        />

        {/* Bucket-Level Metrics */}
        <div>
          <h2 className="text-lg font-semibold mb-3">Bucket-Level Metrics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Bucket Storage (All Teams)</CardTitle>
                <FolderIcon className="h-4 w-4 text-secondary" />
              </CardHeader>
              <CardContent>
                {data.totalSize != null && data.totalFiles != null ? (
                  <>
                    <div className="text-2xl font-bold">{formatBytes(data.totalSize)}</div>
                    <p className="text-xs text-secondary mt-1">
                      {formatNumber(data.totalFiles)} files
                    </p>
                    <p className="text-xs text-secondary mt-1">
                      {data.source === "analytics"
                        ? "From Cloudflare analytics"
                        : "From the complete listing"}
                      {data.measuredAt ? ` at ${new Date(data.measuredAt).toLocaleString()}` : ""}
                      {data.partial ? " (partial: lower bound)" : ""}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="text-2xl font-bold">Not measured yet</div>
                    <p className="text-xs text-secondary mt-1">
                      Press Refresh breakdown to list the bucket once.
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Recent Uploads</CardTitle>
                <CloudArrowUpIcon className="h-4 w-4 text-secondary" />
              </CardHeader>
              <CardContent>
                {data.recentUploads ? (
                  <>
                    <div className="text-2xl font-bold">
                      {formatNumber(data.recentUploads.last24h)}
                    </div>
                    <p className="text-xs text-secondary mt-1">in the last 24 hours</p>
                  </>
                ) : (
                  <div className="text-2xl font-bold">Not available</div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Breakdown by what the bytes are */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Storage Breakdown</h2>
            <p className="text-sm text-secondary">
              {breakdown
                ? `Computed at ${new Date(breakdown.computed_at).toLocaleString()} from ${formatNumber(breakdown.objects)} objects (${formatNumber(breakdown.list_calls)} list calls)`
                : "Not computed yet"}
            </p>
          </div>

          {breakdownQuery.error && (
            <ErrorFallback error={breakdownQuery.error as Error} />
          )}

          {!breakdown && !breakdownQuery.error && (
            <Card>
              <CardContent className="py-6">
                <p className="text-sm text-secondary">
                  {breakdownQuery.isLoading
                    ? "Loading..."
                    : "The per-team breakdown has never been computed. Refreshing lists every object in the bucket once and caches the result; this page never does that on its own."}
                </p>
              </CardContent>
            </Card>
          )}

          {breakdown && (
            <div className="space-y-6">
              {breakdown.partial && (
                <div className="rounded-md border border-border bg-surface p-4 text-sm">
                  <p className="font-medium">Partial listing</p>
                  <p className="text-secondary">
                    {breakdown.partial_reason} Every number below is a lower bound, not a total.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {GROUPS.map((g) => {
                  const t = sumGroup(breakdown.categories, g.categories);
                  const Icon =
                    g.id === "original"
                      ? VideoCameraIcon
                      : g.id === "transcoded"
                        ? FilmIcon
                        : g.id === "cover"
                          ? PhotoIcon
                          : g.id === "hls"
                            ? FolderIcon
                            : ArchiveBoxIcon;
                  return (
                    <Card key={g.id}>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">{g.label}</CardTitle>
                        <Icon className="h-4 w-4 text-secondary" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{formatBytes(t.bytes)}</div>
                        <p className="text-xs text-secondary mt-1">
                          {formatNumber(t.count)} files
                        </p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Storage by kind of object */}
                <Card>
                  <CardHeader>
                    <CardTitle>Storage by Kind</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={groupData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {groupData.map((entry) => (
                            <Cell key={entry.id} fill={GROUP_COLORS[entry.id] ?? GROUP_COLORS.other} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Top Teams by Storage */}
                <Card>
                  <CardHeader>
                    <CardTitle>Top Teams by Storage</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={topTeamsData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis
                          dataKey="name"
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          className="text-xs"
                        />
                        <YAxis tickFormatter={(value) => formatBytes(value, 0)} className="text-xs" />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="size" fill="#3B82F6" name="Storage Size" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>

        {/* Recent Upload Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Upload Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {data.recentUploads ? (
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">{formatNumber(data.recentUploads.last24h)}</p>
                  <p className="text-sm text-secondary">Last 24 hours</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{formatNumber(data.recentUploads.last7d)}</p>
                  <p className="text-sm text-secondary">Last 7 days</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{formatNumber(data.recentUploads.last30d)}</p>
                  <p className="text-sm text-secondary">Last 30 days</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-secondary">
                Not available: upload counts need the Cloudflare Analytics API, which is not
                configured here or did not answer.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Teams Table */}
        {breakdown && (
          <Card>
            <CardHeader>
              <CardTitle>Storage by Team</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-secondary mb-3">
                Every byte in R2, from a complete listing. Rows add up to the bucket total. This is
                not the plan quota: that counter only counts transcoded film, once per source.
              </p>
              <BreakdownTable breakdown={breakdown} />
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
